import React, { useEffect, useState } from "react";
import {
  backfillFxHistory as runFxHistoryBackfill,
  getFxRates,
  triggerFxLiveFetch,
} from "../../data/fxRatesClient.js";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { log } from "../../utils/logger.js";
import { FxBasisControls } from "./FxBasisControls.jsx";

const MAX_FX_HISTORY_CALENDAR_DAYS = 5500;
const TITLE = {
  margin: 0,
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  letterSpacing: -0.2,
};
const BUTTON = {
  background: T.white,
  color: T.blue,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  padding: "4px 12px",
  cursor: "pointer",
};
const PRIMARY_BUTTON = {
  ...BUTTON,
  background: T.blue,
  color: T.white,
  border: "none",
};
const INPUT = {
  width: 160,
  padding: "5px 8px",
  fontSize: T.fsSm,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
};

function formatFetchedAt(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function FxRatesPanel({ currentRole, explainer } = {}) {
  const isAdmin = currentRole === "admin";
  const historyToday = new Date().toISOString().slice(0, 10);
  const historyEarliest = new Date(
    Date.parse(`${historyToday}T00:00:00.000Z`) - (MAX_FX_HISTORY_CALENDAR_DAYS - 1) * 86400000,
  ).toISOString().slice(0, 10);
  const [rates, setRates] = useState(null);
  const [liveSnapshot, setLiveSnapshot] = useState({ fetchedAt: null, rates: {} });
  const [error, setError] = useState(null);
  const [reloads, setReloads] = useState(0);
  const [triggerFetchBusy, setTriggerFetchBusy] = useState(false);
  const [historyFrom, setHistoryFrom] = useState("2026-01-01");
  const [historyTo, setHistoryTo] = useState(historyToday);
  const [historyBackfillBusy, setHistoryBackfillBusy] = useState(false);
  const [historyBackfillResult, setHistoryBackfillResult] = useState(null);
  const [historyBackfillError, setHistoryBackfillError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRates(null);
    (async () => {
      try {
        const out = await getFxRates();
        if (cancelled) return;
        const rawRates = Array.isArray(out?.rates) ? out.rates : [];
        setRates([
          ...rawRates.filter((row) => row.currency === "USD"),
          ...rawRates.filter((row) => row.currency !== "USD"),
        ]);
        setLiveSnapshot(out?.liveSnapshot ?? { fetchedAt: null, rates: {} });
      } catch (cause) {
        if (cancelled) return;
        log.error("FxRatesPanel: getFxRates failed", { name: cause?.name, message: cause?.message });
        setError(cause);
        setRates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [reloads]);

  async function onClickTriggerFetch() {
    if (triggerFetchBusy) return;
    setTriggerFetchBusy(true);
    setError(null);
    try {
      await triggerFxLiveFetch();
      setReloads((count) => count + 1);
    } catch (cause) {
      log.error("[FxRatesPanel] trigger-fetch failed", { name: cause?.name, message: cause?.message });
      setError(new Error("Failed to refresh live rates. Please try again."));
    } finally {
      setTriggerFetchBusy(false);
    }
  }

  async function onClickBackfillHistory() {
    if (historyBackfillBusy) return;
    setHistoryBackfillError(null);
    setHistoryBackfillResult(null);
    const validDate = (value) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const date = new Date(`${value}T00:00:00.000Z`);
      return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    };
    if (!validDate(historyFrom) || !validDate(historyTo) || historyFrom > historyTo) {
      setHistoryBackfillError(new Error("Choose a valid date range with the start date on or before the end date."));
      return;
    }
    if (historyTo > historyToday) {
      setHistoryBackfillError(new Error("The end date cannot be in the future."));
      return;
    }
    const days = Math.floor(
      (Date.parse(`${historyTo}T00:00:00.000Z`) - Date.parse(`${historyFrom}T00:00:00.000Z`)) / 86400000,
    ) + 1;
    if (days > MAX_FX_HISTORY_CALENDAR_DAYS) {
      setHistoryBackfillError(new Error(
        `Choose a date range of ${MAX_FX_HISTORY_CALENDAR_DAYS} calendar days or less.`,
      ));
      return;
    }
    setHistoryBackfillBusy(true);
    try {
      const result = await runFxHistoryBackfill({ from: historyFrom, to: historyTo });
      setHistoryBackfillResult(result);
      setReloads((count) => count + 1);
    } catch (cause) {
      log.error("[FxRatesPanel] FX history backfill failed", { name: cause?.name, status: cause?.status });
      setHistoryBackfillError(new Error(cause?.message || "FX history backfill failed."));
    } finally {
      setHistoryBackfillBusy(false);
    }
  }

  return (
    <div data-testid="fx-rates-panel">
      <h2 style={TITLE}>Currency exchange rates</h2>
      {explainer}
      <FxBasisControls currentRole={currentRole} currencyData={{ rates: rates || [], liveSnapshot }} />

      {error && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <ErrorBanner error={error} onDismiss={() => setError(null)} />
          </div>
          <button type="button" onClick={() => setReloads((count) => count + 1)} style={{ ...PRIMARY_BUTTON, marginLeft: 8 }} data-testid="fx-rates-retry">
            Retry
          </button>
        </div>
      )}
      {rates === null && !error && (
        <div style={{ ...S.emptyState, padding: 24 }} data-testid="fx-rates-loading">
          Loading currency rates…
        </div>
      )}

      {isAdmin && rates?.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: T.fsXs, color: T.mid }} data-testid="fx-live-fetched-note">
            {liveSnapshot.fetchedAt
              ? `Live rates last fetched: ${formatFetchedAt(liveSnapshot.fetchedAt)}`
              : "Live rates not yet fetched"}
          </div>
          <button
            type="button"
            onClick={onClickTriggerFetch}
            disabled={triggerFetchBusy}
            style={{ ...BUTTON, fontSize: T.fsXs, padding: "2px 10px" }}
            data-testid="fx-trigger-fetch-btn"
          >
            {triggerFetchBusy ? "Refreshing…" : "Refresh live rates"}
          </button>
        </div>
      )}

      {isAdmin && (
        <section
          aria-labelledby="fx-history-backfill-title"
          data-testid="fx-history-backfill"
          style={{
            marginTop: 16,
            padding: "14px 16px",
            background: T.lightGray,
            border: `0.5px solid ${T.border}`,
            borderRadius: 6,
          }}
        >
          <h3 id="fx-history-backfill-title" style={{ margin: "0 0 10px", fontSize: T.fsBase, color: T.dark }}>
            Historical FX rates
          </h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: T.fsXs, color: T.mid }}>
              From
              <input type="date" value={historyFrom} min={historyEarliest} max={historyToday} onChange={(event) => setHistoryFrom(event.target.value)} disabled={historyBackfillBusy} data-testid="fx-history-from" style={INPUT} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: T.fsXs, color: T.mid }}>
              To
              <input type="date" value={historyTo} min={historyFrom} max={historyToday} onChange={(event) => setHistoryTo(event.target.value)} disabled={historyBackfillBusy} data-testid="fx-history-to" style={INPUT} />
            </label>
            <button type="button" onClick={onClickBackfillHistory} disabled={historyBackfillBusy} style={PRIMARY_BUTTON} data-testid="fx-history-backfill-btn">
              {historyBackfillBusy ? "Backfilling…" : "Backfill FX history"}
            </button>
          </div>
          <p style={{ margin: "8px 0 0", fontSize: T.fsXs, color: T.mid }}>
            Historical dates only; maximum range is {MAX_FX_HISTORY_CALENDAR_DAYS.toLocaleString()} calendar days.
          </p>
          {historyBackfillBusy && (
            <p role="status" data-testid="fx-history-backfill-progress" style={{ margin: "10px 0 0", fontSize: T.fsSm, color: T.mid }}>
              Fetching historical quotes and saving them to this server’s database…
            </p>
          )}
          {historyBackfillError && (
            <div role="alert" data-testid="fx-history-backfill-error" style={{ marginTop: 10, color: T.red, fontSize: T.fsSm }}>
              {historyBackfillError.message}
            </div>
          )}
          {historyBackfillResult && (
            <div role="status" data-testid="fx-history-backfill-result" style={{ marginTop: 12, fontSize: T.fsSm, color: T.dark }}>
              <strong>
                Backfill complete: {historyBackfillResult.from} to {historyBackfillResult.to} in database {historyBackfillResult.databaseName}.
              </strong>
              <div style={{ marginTop: 4 }}>
                {historyBackfillResult.inserted} rows inserted; {historyBackfillResult.alreadyPresent} already present.
              </div>
              <div style={{ marginTop: 6, fontWeight: T.fwSemibold }}>Date coverage</div>
              <ul style={{ margin: "3px 0 0", paddingLeft: 20 }}>
                {Object.entries(historyBackfillResult.coverageByCurrency || {}).map(([currency, coverage]) => (
                  <li key={currency} data-testid={`fx-history-coverage-${currency}`}>
                    {currency}: {coverage.datesCovered} quote dates
                    {coverage.firstDate ? ` (${coverage.firstDate}–${coverage.lastDate})` : ""}
                  </li>
                ))}
              </ul>
              {(historyBackfillResult.uncoveredCurrencies?.length > 0
                || historyBackfillResult.unsupportedCurrencies?.length > 0
                || historyBackfillResult.failures?.length > 0) ? (
                <div style={{ marginTop: 8 }} data-testid="fx-history-backfill-gaps">
                  <strong>Remaining currency/source gaps:</strong>
                  {historyBackfillResult.uncoveredCurrencies?.length > 0
                    && <div>Uncovered: {historyBackfillResult.uncoveredCurrencies.join(", ")}</div>}
                  {historyBackfillResult.unsupportedCurrencies?.length > 0
                    && <div>No Frankfurter source: {historyBackfillResult.unsupportedCurrencies.join(", ")}</div>}
                  {historyBackfillResult.failures?.length > 0
                    && <div>Source failures: {historyBackfillResult.failures.map((failure) => failure.currency).join(", ")}</div>}
                </div>
              ) : (
                <div style={{ marginTop: 8 }} data-testid="fx-history-backfill-no-gaps">
                  No uncovered currencies or source failures.
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
